import { useMemo, useState } from 'react';
import { ChevronRight, Folder, Gauge } from 'lucide-react';
import { ROTULO_CENTRO_TIPO_NO, type CentroArvoreNo } from '@triade/contracts';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { idsComFilhos, linhasVisiveis, podeCriarFilho, rotuloCentro } from './arvore-logica';
import { minutosParaTexto } from './tipos';

export interface ArvoreCentrosProps {
  /** Arvore da API (raizes com filhos). Ordem natural e aplicada aqui. */
  nos: CentroArvoreNo[] | undefined;
  erro?: unknown;
  onTentar?: () => void;
  selecionadoId: string | undefined;
  mostrarInativos: boolean;
  onMostrarInativos: (mostrar: boolean) => void;
  /** Sem `centro.editar` (FINANCEIRO, ORCAMENTISTA) nao ha "+ raiz" nem "+ filho". */
  editavel: boolean;
  onSelecionar: (no: CentroArvoreNo) => void;
  onNovoFilho: (no: CentroArvoreNo) => void;
  onNovoRaiz: () => void;
}

/**
 * Arvore de centros: sintetico (agrupa) e analitico (recebe minutos, CPM e etapas). Tudo expandido ao abrir; "Expandir tudo" e
 * "Recolher tudo" mudam so a visibilidade. Inativos ficam esmaecidos e so aparecem com "Mostrar inativos" (a API os devolve so
 * quando pedidos). A linha abre o formulario; "+ filho" existe so em sintetico ativo.
 */
export function ArvoreCentros({ nos, erro, onTentar, selecionadoId, mostrarInativos, onMostrarInativos, editavel, onSelecionar, onNovoFilho, onNovoRaiz }: ArvoreCentrosProps) {
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(() => new Set());
  const linhas = useMemo(() => (nos ? linhasVisiveis(nos, recolhidos) : []), [nos, recolhidos]);
  const comFilhos = useMemo(() => (nos ? idsComFilhos(nos) : []), [nos]);

  function alternar(id: string) {
    setRecolhidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  if (erro) return <div className="p-4"><ErroFormacao erro={erro} onTentar={onTentar} titulo="Não foi possível carregar a estrutura" /></div>;
  if (!nos) return <p role="status" className="px-4 py-10 text-center text-small text-ink-muted">Carregando a estrutura de centros…</p>;

  return (
    <div className="flex flex-col">
      <div role="group" aria-label="Ações da estrutura" className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <Button type="button" variant="outline" size="sm" onClick={() => setRecolhidos(new Set())} disabled={recolhidos.size === 0}>
          Expandir tudo
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setRecolhidos(new Set(comFilhos))} disabled={comFilhos.length === 0 || recolhidos.size === comFilhos.length}>
          Recolher tudo
        </Button>
        <label className="flex items-center gap-2 px-1 text-small text-ink-body">
          <input type="checkbox" className="size-4" checked={mostrarInativos} onChange={(e) => onMostrarInativos(e.target.checked)} />
          Mostrar inativos
        </label>
        <span className="hidden text-caption text-ink-muted md:inline">Sintético agrupa · analítico recebe minutos, CPM e etapas</span>
        {editavel && (
          <Button type="button" size="sm" className="ml-auto" onClick={onNovoRaiz}>
            + raiz
          </Button>
        )}
      </div>

      {linhas.length === 0 ? (
        <p role="status" className="px-4 py-10 text-center text-small text-ink-muted">
          Nenhum centro de custo ainda.
          {editavel && ' Crie a raiz da estrutura com "+ raiz".'}
        </p>
      ) : (
        <ul aria-label="Árvore de centros de custo" className="py-1">
          {linhas.map(({ no, nivel, temFilhos, aberto }) => {
            const sel = no.id === selecionadoId;
            const sintetico = no.tipoNo === 'SINTETICO';
            return (
              <li
                key={no.id}
                data-testid={`no-${no.caminho}`}
                data-tipo={no.tipoNo}
                data-ativo={String(no.ativo)}
                className={cn('flex items-center gap-1 border-b border-line-soft pr-2', sel && 'bg-line-soft', !no.ativo && 'opacity-50')}
                style={{ paddingLeft: `${0.5 + nivel * 1.5}rem` }}
              >
                {temFilhos ? (
                  <button
                    type="button"
                    aria-expanded={aberto}
                    aria-label={`${aberto ? 'Recolher' : 'Expandir'} ${rotuloCentro(no)}`}
                    onClick={() => alternar(no.id)}
                    className="grid size-7 shrink-0 place-items-center rounded-md text-ink-muted hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <ChevronRight aria-hidden="true" className={cn('size-4 transition-transform', aberto && 'rotate-90')} />
                  </button>
                ) : (
                  <span aria-hidden="true" className="size-7 shrink-0" />
                )}
                <button
                  type="button"
                  aria-current={sel ? 'true' : undefined}
                  onClick={() => onSelecionar(no)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-ring"
                >
                  <span className="tabular w-14 shrink-0 font-medium text-ink-secondary">{no.caminho}</span>
                  {sintetico ? <Folder aria-hidden="true" className="size-4 shrink-0 text-ink-muted" /> : <Gauge aria-hidden="true" className="size-4 shrink-0 text-primary" />}
                  <span className={cn('truncate', sintetico ? 'font-medium' : 'text-ink-body')}>{no.nome}</span>
                  <span className={cn('shrink-0 rounded-full border px-2 py-px text-caption', sintetico ? 'border-border text-ink-secondary' : 'border-primary/30 text-primary')}>
                    {ROTULO_CENTRO_TIPO_NO[no.tipoNo]}
                  </span>
                  {!no.ativo && <span className="shrink-0 text-caption text-ink-muted">Inativo</span>}
                  {!sintetico && (
                    <span className="tabular ml-auto shrink-0 text-caption text-ink-muted">
                      {no.minutosDisponiveisMes === null ? 'sem minutos' : `${minutosParaTexto(no.minutosDisponiveisMes)} min/mês`}
                    </span>
                  )}
                </button>
                {editavel && podeCriarFilho(no) && (
                  <Button type="button" variant="ghost" size="sm" className="shrink-0" aria-label={`Novo filho de ${rotuloCentro(no)}`} onClick={() => onNovoFilho(no)}>
                    + filho
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
